import type {
  BookingActiveBookingUpdateOfferGetApiData,
  BookingActiveBookingUpdateOfferGetApiResponses,
  BookingAddSalesItemOffersApiData,
  BookingAddSalesItemOffersApiResponses,
  BookingAddSalesItemsApiData,
  BookingAddSalesItemsApiResponses,
  BookingBulkRescheduleApiData,
  BookingBulkRescheduleApiResponses,
  BookingCancelApiData,
  BookingCancelApiResponses,
  BookingCancelSlotReservationApiData,
  BookingCancelSlotReservationApiResponses,
  BookingCreateApiData,
  BookingCreateApiResponses,
  BookingCreateAtGarageApiData,
  BookingCreateAtGarageApiResponses,
  BookingCreateWheelStorageCancelApiData,
  BookingCreateWheelStorageCancelApiResponses,
  BookingDailySeriesGetApiData,
  BookingDailySeriesGetApiResponses,
  BookingEventRecord,
  BookingEventType,
  BookingEventsForBookingGetApiData,
  BookingEventsForBookingGetApiResponses,
  BookingEventsListApiData,
  BookingEventsListApiResponses,
  BookingExportAccountingDataApiData,
  BookingExportAccountingDataApiResponses,
  BookingFilterCountsApiData,
  BookingFilterCountsApiResponses,
  BookingForCalendarListApiData,
  BookingForCalendarListApiResponses,
  BookingForDepartmentSlugListApiData,
  BookingForDepartmentSlugListApiResponses,
  BookingGetCurrentEtaWindowApiData,
  BookingGetCurrentEtaWindowApiResponses,
  BookingGetEditPermissionsApiData,
  BookingGetEditPermissionsApiResponses,
  BookingGetFromSlugApiData,
  BookingGetFromSlugApiResponses,
  BookingListApiData,
  BookingListApiResponses,
  BookingListUpcomingApiData,
  BookingListUpcomingApiResponses,
  BookingMinimalListApiData,
  BookingMinimalListApiResponses,
  BookingOverviewGetApiData,
  BookingOverviewGetApiResponses,
  BookingPatchApiData,
  BookingPatchApiResponses,
  BookingProposalGetBySlugApiData,
  BookingProposalGetBySlugApiResponses,
  BookingProposalListApiData,
  BookingProposalListApiResponses,
  BookingReceiptDownloadApiData,
  BookingReceiptDownloadApiResponses,
  BookingReserveSlotApiData,
  BookingReserveSlotApiResponses,
  BookingTransferOwnershipApiData,
  BookingTransferOwnershipApiResponses,
  BookingTransferOwnershipPreviewApiData,
  BookingTransferOwnershipPreviewApiResponses,
  BookingUncancelApiData,
  BookingUncancelApiResponses,
  BookingUndoUnableToCompleteApiData,
  BookingUndoUnableToCompleteApiResponses,
  BookingUnpaidCreateInvoicesApiData,
  BookingUnpaidCreateInvoicesApiResponses,
  BookingUnpaidListApiData,
  BookingUnpaidListApiResponses,
  BookingUnpaidSumApiData,
  BookingUnpaidSumApiResponses,
  BookingUnpaidToExcelApiData,
  BookingUnpaidToExcelApiResponses,
  BookingUpdateAddressRequestApiData,
  BookingUpdateAddressRequestApiResponses,
  GetBookingForPartnerApiData,
  GetBookingForPartnerApiResponses,
  StationaryBookableServicesApiData,
  StationaryBookableServicesApiResponses
} from '@navio/nsdk';

import { downloadBlobInBrowser, getFilenameFromContentDisposition } from '../../browserBlobDownload';
import { downloadToExcel } from '../../downloadToExcel';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { bookingChoiceUrls } from './bookingChoiceUrls';
import { bookingCompletionUrls } from './bookingCompletionUrls';
import { bookingRecoveryUrls } from './bookingRecoveryUrls';
import { bookingSmsUrls } from './bookingSmsUrls';
import { garageBookingUrls } from './garageBookingUrls';
import { orderEditUrls } from './orderEditUrls';
import { bookingPaymentSessionUrls } from './paymentSession';
import { proposalUrls } from './proposalUrls';
import { workOrderUrls } from './workOrderUrls';

// Bookings

// Registered by `impersonationBypass`, not the domain map, so call sites use `...IgnoringImpersonation`.
export const postCreateBookingEndpoint = responseEndpoint<BookingCreateApiData, BookingCreateApiResponses>({
  getUrl: () => apiUrl('bookings/'),
  getBody: (data) => data
});

export const postCreateBookingAtStationEndpoint = responseEndpoint<
  BookingCreateAtGarageApiData,
  BookingCreateAtGarageApiResponses
>({
  getUrl: () => apiUrl('bookings/station/'),
  getBody: (data) => data
});

export const getBookingUrls = {
  ...bookingCompletionUrls,
  ...bookingRecoveryUrls,
  ...orderEditUrls,
  ...workOrderUrls,
  ...garageBookingUrls,
  ...bookingPaymentSessionUrls,
  ...bookingChoiceUrls,
  getStationaryBookableServices: dataEndpoint<
    StationaryBookableServicesApiData,
    StationaryBookableServicesApiResponses
  >({
    getUrl: (queryParams) => apiUrl('bookings/stationary-bookable-services/', queryParams)
  }),
  getBookingMinimalList: dataEndpoint<BookingMinimalListApiData, BookingMinimalListApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/minimal/', queryParams)
  }),
  getUnpaidBookings: dataEndpoint<BookingUnpaidListApiData, BookingUnpaidListApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/unpaid/', queryParams)
  }),
  getUnpaidBookingsToExcel: dataEndpoint<BookingUnpaidToExcelApiData, BookingUnpaidToExcelApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/unpaid-to-excel/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  postCreateInvoiceForUnpaidBookings: responseEndpoint<
    BookingUnpaidCreateInvoicesApiData,
    BookingUnpaidCreateInvoicesApiResponses
  >({
    getUrl: () => apiUrl('bookings/unpaid-create-invoices/'),
    getBody: (data) => data
  }),
  getTotalUnpaidBookingsAmount: dataEndpoint<BookingUnpaidSumApiData, BookingUnpaidSumApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/unpaid-amount/', queryParams)
  }),
  getPaginatedBookingOverviewDetailsList: dataEndpoint<
    BookingForCalendarListApiData,
    BookingForCalendarListApiResponses
  >({
    getUrl: (queryParams) => apiUrl('bookings/for-calendar/', queryParams)
  }),
  patchCancelBooking: responseEndpoint<BookingCancelApiData, BookingCancelApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/cancel/`),
    getBody: (data) => data
  }),
  patchUncancelBooking: responseEndpoint<BookingUncancelApiData, BookingUncancelApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/uncancel/`)
  }),
  patchUndoUnableToComplete: responseEndpoint<
    BookingUndoUnableToCompleteApiData,
    BookingUndoUnableToCompleteApiResponses
  >({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/undo-unable-to-complete/`)
  }),
  getBookingsOverview: dataEndpoint<BookingOverviewGetApiData, BookingOverviewGetApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/overview/', queryParams)
  }),
  getBookingDailySeries: dataEndpoint<BookingDailySeriesGetApiData, BookingDailySeriesGetApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/daily-series/', queryParams)
  }),
  getPaginatedUpcomingBookings: dataEndpoint<BookingListUpcomingApiData, BookingListUpcomingApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/upcoming/', queryParams)
  }),
  getPaginatedBookingEvents: dataEndpoint<BookingEventsListApiData, BookingEventsListApiResponses>({
    getUrl: (queryParams) => apiUrl('booking-events/', queryParams)
  }),
  getEventsForBooking: dataEndpoint<BookingEventsForBookingGetApiData, BookingEventsForBookingGetApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`booking-events/${bookingId}/`)
  }),
  getPaginatedBookings: dataEndpoint<BookingListApiData, BookingListApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/', queryParams)
  }),
  getBookingFilterCounts: dataEndpoint<BookingFilterCountsApiData, BookingFilterCountsApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/filter-counts/', queryParams)
  }),
  getPaginatedBookingsForDepartmentSlug: dataEndpoint<
    BookingForDepartmentSlugListApiData,
    BookingForDepartmentSlugListApiResponses
  >({
    getUrl: ({ departmentSlug, ...queryParams }) => apiUrl(`bookings/for-department/${departmentSlug}/`, queryParams)
  }),
  getEditBookingPermissions: dataEndpoint<BookingGetEditPermissionsApiData, BookingGetEditPermissionsApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/edit-permissions/`)
  }),
  getBookingForPartner: dataEndpoint<GetBookingForPartnerApiData, GetBookingForPartnerApiResponses>({
    getUrl: ({ bookingId, ...queryParams }) => apiUrl(`bookings/${bookingId}/for-partner/`, queryParams)
  }),
  postCreateWheelStorageCancelBooking: responseEndpoint<
    BookingCreateWheelStorageCancelApiData,
    BookingCreateWheelStorageCancelApiResponses
  >({
    getUrl: () => apiUrl('bookings/create-wheel-storage-cancel/'),
    getBody: (data) => data
  }),
  postAddSalesItemsToBooking: responseEndpoint<BookingAddSalesItemsApiData, BookingAddSalesItemsApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/add-sales-items/`),
    getBody: (data) => data
  }),
  postAddSalesItemOffersToBooking: responseEndpoint<
    BookingAddSalesItemOffersApiData,
    BookingAddSalesItemOffersApiResponses
  >({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/add-sales-item-offers/`),
    getBody: (data) => data
  }),
  postBookingTransferOwnership: responseEndpoint<BookingTransferOwnershipApiData, BookingTransferOwnershipApiResponses>(
    {
      getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/transfer-ownership/`),
      getBody: (data) => data
    }
  ),
  getBookingTransferOwnershipPreview: dataEndpoint<
    BookingTransferOwnershipPreviewApiData,
    BookingTransferOwnershipPreviewApiResponses
  >({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/transfer-ownership/preview/`)
  }),
  downloadBookingReceipt: dataEndpoint<BookingReceiptDownloadApiData, BookingReceiptDownloadApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/download-receipt/`),
    responseType: 'blob',
    sideEffectGet: true,
    handleRes: async (res) => {
      const blob = res.data;
      const filename = getFilenameFromContentDisposition(res.headers['content-disposition'], 'receipt.pdf');
      downloadBlobInBrowser(blob, filename);
      return blob;
    }
  }),
  ...bookingSmsUrls,
  getPotentialConflictsOnAddressChange: dataEndpoint<
    BookingUpdateAddressRequestApiData,
    BookingUpdateAddressRequestApiResponses
  >({
    getUrl: ({ bookingId, addressId }) => apiUrl(`bookings/${bookingId}/update-address-request/`, { addressId })
  }),
  getBookingInfo: dataEndpoint<BookingGetFromSlugApiData, BookingGetFromSlugApiResponses>({
    getUrl: ({ slug }) => apiUrl(`bookings/info-from-slug/${slug}/`)
  }),
  getCurrentEstimatedArrivalTime: dataEndpoint<
    BookingGetCurrentEtaWindowApiData,
    BookingGetCurrentEtaWindowApiResponses
  >({
    getUrl: ({ slug }) => apiUrl(`bookings/info-from-slug/${slug}/current-estimated-arrival-window/`)
  }),
  patchBooking: responseEndpoint<BookingPatchApiData, BookingPatchApiResponses>({
    getUrl: ({ bookingId }) => apiUrl(`bookings/${bookingId}/`),
    getBody: ({ bookingId: _, ...body }) => body
  }),
  getExportAccountingData: dataEndpoint<BookingExportAccountingDataApiData, BookingExportAccountingDataApiResponses>({
    getUrl: (queryParams) => apiUrl('bookings/export-accounting-data/', queryParams),
    handleRes: async (res) => downloadToExcel(res),
    responseType: 'blob',
    sideEffectGet: true
  }),
  getBookingProposalBySlug: dataEndpoint<BookingProposalGetBySlugApiData, BookingProposalGetBySlugApiResponses>({
    getUrl: ({ slug }) => apiUrl(`booking-proposals/${slug}/`)
  }),
  ...proposalUrls,
  getPaginatedBookingProposals: dataEndpoint<BookingProposalListApiData, BookingProposalListApiResponses>({
    getUrl: (queryParams) => apiUrl('booking-proposals/', queryParams)
  }),
  postBulkRescheduleBookings: responseEndpoint<BookingBulkRescheduleApiData, BookingBulkRescheduleApiResponses>({
    getUrl: () => apiUrl('bookings/bulk-reschedule/'),
    getBody: (data) => data
  }),
  getBookingActiveBookingUpdateOffer: dataEndpoint<
    BookingActiveBookingUpdateOfferGetApiData,
    BookingActiveBookingUpdateOfferGetApiResponses
  >({
    getUrl: ({ slug }) => apiUrl(`bookings/${slug}/active-booking-update-offer/`)
  }),
  postReserveSlot: responseEndpoint<BookingReserveSlotApiData, BookingReserveSlotApiResponses>({
    getUrl: () => apiUrl('bookings/station/reserve-slot/'),
    getBody: (data) => data
  }),
  postCancelSlotReservation: responseEndpoint<
    BookingCancelSlotReservationApiData,
    BookingCancelSlotReservationApiResponses
  >({
    getUrl: () => apiUrl('bookings/station/cancel-slot-reservation/'),
    getBody: (data) => data
  })
};

export type { BookingEventRecord, BookingEventType };

// Re-exported so the resource discovery in uniqueEndpointKeys.test.ts sees these keys.
export { getBookingProposalNoteUrls } from './proposalNoteUrls';
